import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { Trash2 } from 'lucide-react';
import { Appointment } from '@/services/features/appointmentSlotsService.ts';
import PaginationComponent from '@/components/common/PaginationComponent';
import { setAndformatDateLocally } from '@/utils/dateUtils';

interface AppointmentSlotsTableComponentProps {
  appointments: Appointment[];
  isLoading: boolean;
  currentPage: number;
  rowsPerPage: number;
  hasMoreData: boolean;
  totalRecords?: number;
  onDeleteAppointment: (appointmentId: number) => void;
  onRowsPerPageChange: (value: string) => void;
  onPageChange: (page: number) => void;
}

const AppointmentSlotsTableComponent: React.FC<AppointmentSlotsTableComponentProps> = ({
  appointments,
  isLoading,
  currentPage,
  rowsPerPage,
  hasMoreData,
  totalRecords = 0,
  onDeleteAppointment,
  onRowsPerPageChange,
  onPageChange,
}) => {
  // Calculate pagination values for server-side pagination
  const totalPages = Math.ceil(totalRecords / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalRecords);
  
  const handleRecordsPerPageChange = (newRecordsPerPage: number) => {
    onRowsPerPageChange(newRecordsPerPage.toString());
  };
  
  return (
    <Card className="bg-white shadow-lg border-0 rounded-2xl overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-purple-50/50">
            <TableHead className="text-purple-900 font-semibold">Clinic Name</TableHead>
            <TableHead className="text-purple-900 font-semibold">Facility Name</TableHead>
            <TableHead className="text-purple-900 font-semibold">Appointment Date</TableHead>
            <TableHead className="text-purple-900 font-semibold">Appointment Start Time</TableHead>
            <TableHead className="text-purple-900 font-semibold">Appointment End Time</TableHead>
            <TableHead className="text-purple-900 font-semibold">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8">
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                </div>
              </TableCell>
            </TableRow>
          ) : totalRecords === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8">
                <p className="text-gray-500">No appointments found</p>
              </TableCell>
            </TableRow>
          ) : (
            appointments.map((appointment, idx) => (
              <TableRow key={appointment.appointment_id} className="hover:bg-gray-50">
                <TableCell className="font-medium">{appointment.clinic_name}</TableCell>
                <TableCell>{appointment.facility_name}</TableCell>
                <TableCell>
                  {setAndformatDateLocally(appointment.appointment_date)}
                </TableCell>
                <TableCell>{appointment.appointment_start_time}</TableCell>
                <TableCell>{appointment.appointment_end_time}</TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    aria-label={`Delete appointment on ${appointment.appointment_date}`}
                    size="icon"
                    onClick={() => onDeleteAppointment(appointment.appointment_id)}
                    disabled={appointment.patient_clinic_data_id !== null}
                    className={`${appointment.patient_clinic_data_id !== null
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-red-500 hover:bg-red-50"
                      }`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Pagination Controls */}
      {totalRecords > 0 && (
        <div className="border-t border-gray-100">
          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            recordsPerPage={rowsPerPage}
            totalRecords={totalRecords}
            startIndex={startIndex}
            endIndex={endIndex}
            onPageChange={onPageChange}
            onRecordsPerPageChange={handleRecordsPerPageChange}
            className="bg-white"
          />
        </div>
      )}
    </Card>
  );
};

export default AppointmentSlotsTableComponent;